/**
 * @typedef {{
 * 	date: string | null,
 * 	location: {
 * 		address: string,
 * 		latitude: number | null,
 * 		longitude: number | null
 * 	},
 * 	price: number | null,
 * 	property: {
 * 		bathroomCount: number | null,
 * 		bedroomCount: number | null,
 * 		squareFootage: number | null
 * 	},
 * 	status: string
 * }} Listing
 * @typedef {{
 * 	readonly property: {
 * 		agentId?: string | null,
 * 		agentName?: string | null,
 * 		comparables: { comparables: Listing[], listed: Listing[], sold: Listing[] },
 * 		selectedProperty?: {
 * 			address?: {
 * 				city?: string | null,
 * 				country?: string | null,
 * 				postalCode?: string | null,
 * 				state?: string | null,
 * 				streetAddress1?: string | null,
 * 				streetAddress2?: string | null
 * 			} | null
 * 		} | null
 * 	},
 * 	readonly propertyService: {
 * 		requestAccurateValuation: () => Promise<void>
 * 	},
 * 	readonly isBelowBreakpoint: boolean
 * }} Props
 * @typedef {{
 * 	readonly hoveredListingAddress: string | null,
 * 	readonly seeMore: boolean
 * }} State
 */

import { translate } from '../../../App/Internationalization';
import { withProperty } from '../PropertyProvider';
import { withBreakpoint } from '../../../shared/BreakpointProvider';
import { withPropertyService } from 'service-container';
import BaseButton from '../../../shared/BaseButton/BaseButton';
import ComparableMarketMap from './ComparableMarketMap';
import ComparableMarketProperty from './ComparableMarketProperty';
import ComparableMarketValuationRequest from './ComparableMarketValuationRequest';
import images from '../../../config/local/images';
import Loading from '../../../shared/Loading';
import NoContent from '../../../shared/NoContent';
import React from 'react';

/** @extends {React.Component<Props, State>} */
class ComparableMarketCard extends React.Component {
	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			hoveredListingAddress: null,
			seeMore: false,
		};

		this.handleToggle = this.handleToggle.bind(this);
		this.handleMarkerHover = this.handleMarkerHover.bind(this);
		this.handleRequestValuationReport =
			this.handleRequestValuationReport.bind(this);
	}

	/**
	 * @param {Listing} property
	 * @param {number} index
	 * @param {string | null} hoveredListingAddress
	 * @returns {React.ReactElement | null}
	 */
	static seeLess(property, index, hoveredListingAddress) {
		if (index >= 3) {
			return null;
		}

		return (
			<ComparableMarketProperty
				comparable={property}
				isHighlighted={
					hoveredListingAddress === property.location.address
				}
				key={index}
			/>
		);
	}

	/**
	 * @param {Listing} property
	 * @param {number} index
	 * @param {string | null} hoveredListingAddress
	 * @returns {React.ReactElement}
	 */
	static seeMore(property, index, hoveredListingAddress) {
		return (
			<ComparableMarketProperty
				comparable={property}
				isHighlighted={
					hoveredListingAddress === property.location.address
				}
				key={index}
			/>
		);
	}

	/**
	 * @returns {void}
	 */
	handleToggle() {
		this.setState((prevState) => ({
			seeMore: !prevState.seeMore,
		}));
	}

	/**
	 * @param {string | null} hoveredListingAddress
	 * @returns {void}
	 */
	handleMarkerHover(hoveredListingAddress) {
		if (this.state.hoveredListingAddress === hoveredListingAddress) {
			return;
		}
		this.setState({ hoveredListingAddress });
	}

	/**
	 * @returns {Promise<void>}
	 */
	async handleRequestValuationReport() {
		await this.props.propertyService.requestAccurateValuation();
	}

	/**
	 * @returns {boolean}
	 */
	hasValuationAddress() {
		const agentId = this.props.property.agentId;
		const address = this.props.property.selectedProperty?.address;

		return Boolean(
			agentId && address?.streetAddress1 && address.city && address.state
		);
	}

	/**
	 * @param {{
	 * 	agentName: string | null | undefined,
	 * 	canRequestValuation: boolean,
	 * 	handleMarkerHover: ((address: string | null) => void) | undefined,
	 * 	hoveredListingAddress: string | null,
	 * 	listings: Listing[],
	 * 	seeMore: boolean,
	 * 	title: string
	 * }} options
	 * @returns {React.ReactElement | null}
	 */
	static renderSection(options) {
		const {
			agentName,
			canRequestValuation,
			handleMarkerHover,
			handleRequestValuationReport,
			hoveredListingAddress,
			isBelowBreakpoint,
			listings,
			seeMore,
			title,
		} = options;

		if (!listings.length) {
			return null;
		}

		const visibleListings = seeMore ? listings : listings.slice(0, 3);
		const description = ComparableMarketCard.getSectionDescription(title);
		const buttonContent =
			ComparableMarketCard.getSectionButtonContent(agentName);
		const isStackedLayout = isBelowBreakpoint;

		return (
			<section className="mb-10" key={title}>
				<h4 className="h-ms mb-2">{title}</h4>

				<p
					className="-text-ms-1 mb-4"
					style={{
						marginBottom: '1rem',
						textAlign: 'left',
					}}
				>
					{description}
				</p>

				<div
					className={
						isStackedLayout
							? 'flex flex-col gap-4'
							: 'flex gap-4 items-start'
					}
				>
					<div
						className="min-w-0 flex-1"
						style={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							maxWidth: isStackedLayout ? '100%' : '27rem',
						}}
					>
						<ul
							className="list-group bulleted-none"
							style={{
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{visibleListings.map((property, index) => {
								if (seeMore) {
									return ComparableMarketCard.seeMore(
										property,
										index,
										hoveredListingAddress
									);
								}

								return ComparableMarketCard.seeLess(
									property,
									index,
									hoveredListingAddress
								);
							})}
						</ul>
					</div>

					<aside
						className="flex-shrink-0 w-full"
						style={{
							alignItems: 'center',
							display: 'flex',
							flexDirection: 'column',
							marginLeft: isStackedLayout ? 0 : '8px',
							maxWidth: isStackedLayout ? '100%' : '24rem',
							width: '100%',
						}}
					>
						<ComparableMarketMap
							listings={visibleListings}
							onMarkerHover={handleMarkerHover}
						/>

						<ComparableMarketValuationRequest
							buttonLabel={buttonContent.label}
							canRequestValuation={canRequestValuation}
							description={buttonContent.description}
							onRequestValuation={handleRequestValuationReport}
						/>
					</aside>
				</div>
			</section>
		);
	}

	/**
	 * @param {string} title
	 * @returns {string}
	 */
	static getSectionDescription(title) {
		switch (title) {
			case 'Recently Sold':
				return "Recently Sold properties are auto-generated based on location and similarities to your property. To receive an accurate estimate of your home's value, contact your real estate professional.";

			case 'Just Listed':
				return "Just Listed properties are auto-generated based on location and similarities to your property. To receive an accurate estimate of your home's value, contact your real estate professional.";

			default:
				return "Comparable properties are auto-generated based on location and similarities to your property. To receive an accurate estimate of your home's value, contact your real estate professional.";
		}
	}

	/**
	 * @param {string | null | undefined} agentName
	 * @returns {{ description: string, label: string }}
	 */
	static getSectionButtonContent(agentName) {
		return {
			description: `Get an expert analysis of your home value from ${
				agentName ?? 'your real estate professional'
			}.`,
			label: translate(
				'homeowner.pages.finances.view.estimate.request.button'
			),
		};
	}

	/**
	 * @returns {{ listings: Listing[], title: string }[]}
	 */
	getSections() {
		return [
			{
				listings: this.props.property.comparables.sold,
				title: 'Recently Sold',
			},
			{
				listings: this.props.property.comparables.listed,
				title: 'Just Listed',
			},
			{
				listings: this.props.property.comparables.comparables,
				title: 'Comparable Properties',
			},
		];
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	render() {
		if (this.props.property.comparables.comparables === undefined) {
			return <Loading />;
		}

		const sections = this.getSections().filter(
			(section) => section.listings.length > 0
		);

		if (sections.length < 1) {
			return (
				<section className="mt-10">
					<header className="h-ms-4 mb-2">
						{translate(
							'homeowner.pages.finances.view.market.title'
						)}
					</header>

					<NoContent />
				</section>
			);
		}

		return (
			<section className="mt-10">
				<header className="h-ms-4 mb-2">
					{translate('homeowner.pages.finances.view.market.title')}
				</header>

				{sections.map((section) =>
					ComparableMarketCard.renderSection({
						agentName: this.props.property.agentName,
						canRequestValuation: this.hasValuationAddress(),
						handleMarkerHover: this.handleMarkerHover,
						handleRequestValuationReport:
							this.handleRequestValuationReport,
						hoveredListingAddress: this.state.hoveredListingAddress,
						isBelowBreakpoint: this.props.isBelowBreakpoint,
						listings: section.listings,
						seeMore: this.state.seeMore,
						title: section.title,
					})
				)}

				<footer>
					<BaseButton
						className="mx-auto"
						icon={images.icons.seeMore}
						onClick={this.handleToggle}
						outline={true}
					>
						{this.state.seeMore
							? translate(
									'homeowner.pages.finances.view.see_less'
							  )
							: translate(
									'homeowner.pages.finances.view.see_more'
							  )}
					</BaseButton>
				</footer>
			</section>
		);
	}
}

export default withBreakpoint(
	withPropertyService(withProperty(ComparableMarketCard))
);
